import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { IconComponent, type IconName } from '@lite-workspace/shared/blocks/icon';
import { FileDropZoneDirective } from './file-drop-zone.directive';

/** How long the refusal holds the zone. Long enough to read the line without hunting for it. */
const REJECT_MS = 4000;

/**
 * Dashed drop-zone tile: one label that both takes a drop and opens the system picker by click.
 * The ONE home of that look — a second hand-written copy drifts in padding and tone (design-from-system-first
 * § Look-alike gate). The hidden `<input>` lives here too: every consumer filtered its own `change`
 * event with the same three lines, and the zone already owns the drop filter.
 */
@Component({
  selector: 'hlm-file-drop-zone-tile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FileDropZoneDirective, IconComponent],
  host: { class: 'block' },
  template: `
    <label
      hlmFileDropZone
      #dz="fileDropZone"
      [hlmFileDropZoneAccept]="typePrefixes()"
      [hlmFileDropZoneAcceptExtensions]="extensions()"
      (filesDropped)="emit($event)"
      (filesRejected)="reject()"
      [class]="zoneClass(dz.active())"
    >
      <input type="file" [accept]="accept()" [multiple]="multiple()" class="hidden" (change)="onInput($event)" />
      @if (rejected()) {
        <app-icon name="image-off" [size]="28" class="text-bad" />
        <span class="max-w-75 text-base font-semibold text-bad">{{ rejectMessage() }}</span>
      } @else {
        <app-icon [name]="icon()" [size]="28" class="text-ph" />
        <span class="text-base font-semibold text-ink">{{ label() }}</span>
        @if (hint()) {
          <span class="max-w-75 text-xs leading-relaxed text-c3">{{ hint() }}</span>
        }
        @if (pill()) {
          <span
            class="mt-1 inline-flex h-5.5 items-center rounded-[var(--r-chip)] border border-warn-line bg-warn-soft
                   px-2 text-xs font-semibold text-warn"
          >
            {{ pill() }}
          </span>
        }
      }
    </label>
  `,
})
export class FileDropZoneTileComponent {
  readonly icon = input.required<IconName>();
  /** Not `title`: a static `title="…"` on the host would also render the browser's own OS tooltip. */
  readonly label = input.required<string>();
  readonly hint = input('');
  /** Short constraint chip under the hint («JPG · один файл»); empty — no chip. */
  readonly pill = input('');
  /** Native `accept` of the hidden input — what the SYSTEM picker offers. */
  readonly accept = input('image/*');
  readonly multiple = input(false);
  /** Drop filter, shared by the drop zone and the hidden input: MIME prefixes + extra extensions. */
  readonly typePrefixes = input<readonly string[]>(['image/']);
  readonly extensions = input<readonly string[]>([]);
  /** Shown INSIDE the zone for {@link REJECT_MS} when a file the filter refuses arrives. */
  readonly rejectMessage = input('Такой файл не подойдёт');
  readonly filesDropped = output<File[]>();

  protected readonly rejected = signal(false);
  private timer?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.timer));
  }

  protected zoneClass(active: boolean): string {
    const base =
      'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-9 text-center';
    if (this.rejected()) return `${base} border-bad-line bg-bad-soft`;
    return active ? `${base} border-acc bg-acc-softer` : `${base} border-line2 bg-panel hover:border-acc-line`;
  }

  protected onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.submit(Array.from(input.files ?? []));
    // Same file picked twice in a row fires no `change` without this reset.
    input.value = '';
  }

  /**
   * Files picked OUTSIDE the zone (a host button that opens the system picker itself) go through the
   * same gate as a drop and the hidden input: the zone is the one place that filters by type and the one
   * surface that says «no» — a host that emitted the picker result on its own would skip both halves.
   */
  submit(picked: readonly File[]): void {
    const files = picked.filter(file => this.accepted(file));
    if (files.length > 0) this.emit(files);
    else if (picked.length > 0) this.reject();
  }

  protected emit(files: File[]): void {
    if (files.length === 0) return;
    // A good file cancels a refusal still on screen — otherwise the error outlives what it described.
    clearTimeout(this.timer);
    this.rejected.set(false);
    this.filesDropped.emit(files);
  }

  /** Second refusal re-arms the window from scratch instead of inheriting the first one's remainder. */
  protected reject(): void {
    clearTimeout(this.timer);
    this.rejected.set(true);
    this.timer = setTimeout(() => this.rejected.set(false), REJECT_MS);
  }

  private accepted(file: File): boolean {
    return (
      this.typePrefixes().some(prefix => file.type.startsWith(prefix)) ||
      this.extensions().some(extension => file.name.toLowerCase().endsWith(`.${extension}`))
    );
  }
}
